import * as React from 'react';

import { useQuery } from '@apollo/client';

import user from 'js/lib/user';

import type Schedule from 'bundles/course-sessions/models/Schedule';
import useCourseSchedule from 'bundles/course-v2/providers/CourseNavigationProvider/useCourseSchedule';
import type {
  CourseNavigationQuery,
  CourseNavigationQueryVariables,
} from 'bundles/course-v2/queries/courseNavigationQuery/__generated__/courseNavigationQuery';
import courseNavigationQuery from 'bundles/course-v2/queries/courseNavigationQuery/courseNavigationQuery.graphql';
import type { WeeksNavigationItem } from 'bundles/course-v2/types/CourseNavigation';
import { courseNavigationV2ToCourseNavigationV1 } from 'bundles/course-v2/utils/courseNavigationV2ToCourseNavigationV1';
import type { CourseNavigationV1 } from 'bundles/naptimejs/resources/__generated__/CourseNavigationV1';

type Props = React.PropsWithChildren<{
  courseId: string;
}>;

type ContextData = {
  /**
   * Combined user and course id
   */
  id?: string;

  /**
   * Course navigation items in the shape of CourseNavigationV1
   * tldr: it should be only simplified when the week -> module migration is over
   * @see bundles/course-v2/utils/courseNavigationV2ToCourseNavigationV1 for details
   */
  navigationItems?: CourseNavigationV1['navigationItems'];
  /**
   * Course navigation v2 current module
   * Note: it can be `0`. For course preview
   */
  currentModule?: number | null;

  loading: boolean;
};

export type CourseNavigationContextType = ContextData;

const CourseNavigationContext = React.createContext<ContextData | null>(null);

const courseScheduleToWeekNavigationItems = (courseSchedule: Schedule) => {
  return {
    typeName: 'weeksNavigationItem',
    definition: {
      currentWeekNumber: 1,
      weeks: Object.keys(courseSchedule?.weekMap).map(() => ({
        weekStatus: 'ONTRACK',
      })),
    },
  } as WeeksNavigationItem;
};
/**
 * Course Navigation is a tier 2 service with a lot of downstream dependencies so in the case of it failing
 * we use onDemandCourseSchedule as a backup. This will not have the current week information but showing
 * all weeks as on track is better than not showing the course navigation at all
 */
const CourseNavigationProvider = ({ children, courseId }: Props) => {
  const courseNavigation = useQuery<CourseNavigationQuery, CourseNavigationQueryVariables>(courseNavigationQuery, {
    variables: { userId: String(user.get().id), courseId },
    fetchPolicy: 'cache-and-network',
    context: { clientName: 'gatewayGql' },
    skip: !courseId,
  });
  const courseSchedule = useCourseSchedule({ courseId });

  const courseNavigationResults = courseNavigation.data?.CourseNavigation?.getCourseNavigationByUserIdCourseId;

  const navigationItemsFromCourseNavigation = courseNavigationResults
    ? courseNavigationV2ToCourseNavigationV1(courseNavigationResults)
    : undefined;

  // Only use course schedule as backup if there is no course navigation data and course nav isn't loading
  const combinedLoadingState = courseNavigation.loading || (!courseNavigationResults && courseSchedule.loading);
  const shouldUseBackupWeeksNavigationItem =
    !navigationItemsFromCourseNavigation && !combinedLoadingState && courseSchedule.schedule;
  const backupWeeksNavigationItem = {
    navigationItems: shouldUseBackupWeeksNavigationItem
      ? [courseScheduleToWeekNavigationItems(courseSchedule.schedule)]
      : [],
  };
  const combinedCourseNavigationItems = navigationItemsFromCourseNavigation || backupWeeksNavigationItem;

  return (
    <CourseNavigationContext.Provider
      value={{
        ...combinedCourseNavigationItems,
        currentModule: courseNavigationResults?.modules?.currentModule,
        loading: combinedLoadingState,
      }}
    >
      {children}
    </CourseNavigationContext.Provider>
  );
};

export const useCourseNavigationContext = () => {
  const context = React.useContext(CourseNavigationContext);
  if (!context) {
    throw new Error('useCourseNavigationContext must be used within a CourseNavigationProvider');
  }
  return context;
};

export default CourseNavigationProvider;
